import RouterPathProvider from "@core/RouterPath/RouterPathProvider";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import Workspace from "@core-ui/ContextServices/Workspace";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { refreshPage } from "@core-ui/utils/initGlobalFuncs";
import { toManagedStoragePathname } from "@ext/enterprise/pathname/toManagedStoragePathname";
import { getClientServerUrl } from "@ext/enterprise/utils/availableCatalogsServer";
import { getEnterpriseSourceData } from "@ext/enterpriseCommon/logic/storage/getEnterpriseSourceData";
import CloneModal from "@ext/git/actions/Clone/components/CloneModal";
import getClonePathname from "@ext/git/actions/Clone/logic/getClonePathname";
import { useCloneRepo } from "@ext/git/actions/Clone/logic/useCloneRepo";
import type { CatalogLink } from "@ext/navigation/NavigationLinks";
import getStorageNameByData from "@ext/storage/logic/utils/getStorageNameByData";
import { useState } from "react";

const useAvailableCatalogClone = (link: CatalogLink) => {
	const { isWeb, isTauri, isNext } = usePlatform();
	const [showFallbackForm, setShowFallbackForm] = useState(false);

	const { conf } = PageDataContextService.value;
	const workspace = Workspace.current();
	const serverUrl = getClientServerUrl(conf.enterpriseCloud, workspace?.enterprise?.gesUrl)?.url;
	const source = getEnterpriseSourceData(SourceDataService.value ?? [], serverUrl);
	const group = link.repo?.split("/").slice(0, -1).join("/") ?? "";
	const storageData = source ? { source, group, name: link.name } : undefined;
	const { isReadOnly } = conf;
	const redirectOnClone = isReadOnly
		? RouterPathProvider.getReadOnlyPathname(link.name)
		: storageData &&
			// A GES or GES Cloud catalog opens by the short pathname its home page card leads to, as the server builds it.
			toManagedStoragePathname(
				workspace,
				getStorageNameByData(source),
				getClonePathname(storageData, link.branch),
			);

	const { startClone } = useCloneRepo({
		storageData,
		branch: link.branch,
		skipCheck: true,
		isBare: isNext,
		skipLfsPull: isWeb || isTauri,
		redirectOnClone,
		onStart: refreshPage,
		onError: refreshPage,
	});

	const start = () => {
		if (!source) {
			setShowFallbackForm(true);
			return;
		}
		void startClone();
	};

	const fallback = showFallbackForm ? <CloneModal onClose={() => setShowFallbackForm(false)} /> : null;

	return { start, fallback };
};

export default useAvailableCatalogClone;
